import { useCallback, useEffect, useState } from "react"
import { keepPreviousData, useQuery } from "@tanstack/react-query"

import { v4CustomersGeocode } from "@be-miniapp/api"
import type { Location } from "@be-miniapp/types"

import { usePointsOfInterest } from "../queries/use-points-of-interest"
import type { RideLocation } from "../stores/ride-data"

/** Shared default so AppView and Zalo use the same POI / reverse-geocode cadence while dragging the pin. */
export const CONFIRM_ADDRESS_MAP_CENTER_DEBOUNCE_MS = 300

/**
 * Map + geocode queries for confirm-address.
 * Debounces the map center for POI + reverse geocode (same timing on both apps unless overridden).
 */
export function useConfirmAddressMapQueries(options: {
  defaultPickup: RideLocation | undefined
  /** @default CONFIRM_ADDRESS_MAP_CENTER_DEBOUNCE_MS */
  queryCenterDebounceMs?: number
}) {
  const {
    defaultPickup,
    queryCenterDebounceMs = CONFIRM_ADDRESS_MAP_CENTER_DEBOUNCE_MS,
  } = options

  const [currentCenter, setCurrentCenter] = useState<Location | null>(null)
  const [queryCenter, setQueryCenter] = useState<Location | null>(null)

  useEffect(() => {
    if (queryCenterDebounceMs <= 0) {
      setQueryCenter(currentCenter)
      return
    }
    const id = window.setTimeout(() => {
      setQueryCenter(currentCenter)
    }, queryCenterDebounceMs)
    return () => window.clearTimeout(id)
  }, [currentCenter, queryCenterDebounceMs])

  const onCenterChange = useCallback((center: { lat: number; lng: number }) => {
    setCurrentCenter({ latitude: center.lat, longitude: center.lng })
  }, [])

  const { nearbyPickups } = usePointsOfInterest({
    point: queryCenter,
  })

  const { data: firstPointGeocode, isPending } = useQuery({
    queryKey: [
      "firstPointGeocode",
      queryCenter?.latitude,
      defaultPickup?.location?.latitude,
      queryCenter?.longitude,
      defaultPickup?.location?.longitude,
    ],
    queryFn: async () => {
      const latitude =
        queryCenter?.latitude ?? defaultPickup?.location?.latitude ?? 0
      const longitude =
        queryCenter?.longitude ?? defaultPickup?.location?.longitude ?? 0
      return v4CustomersGeocode({
        latitude,
        longitude,
        location: { latitude, longitude },
      })
    },

    enabled: !!queryCenter || (defaultPickup?.location?.latitude ?? 0) > 0,
    placeholderData: keepPreviousData,
  })

  return {
    onCenterChange,
    nearbyPickups,
    firstPointGeocode,
    isPending,
  }
}
